/* eslint-disable react/jsx-props-no-spreading */
/**
 * @fileoverview Componente Molecule para la gestión y edición de tags del cliente en el inbox.
 * Permite seleccionar, crear y guardar tags asociados al cliente, integrando lógica, UI y validación.
 * @module CustomerDetailTagsMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 2.1.0
 */
import React, { memo, useCallback } from "react";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ButtonComponent from "../../../components/ButtonMC";
import ConversationTagsMC from "./ConversationTagsMC";
import MultiSelectDropdownMC from "../../../components/MultiSelectDropdownMC";
import useCustomerDetailTagsData from "../../../hooks/inbox/data/useCustomerDetailTagsData";
import useCustomerDetailTagsActions from "../../../hooks/inbox/actions/useCustomerDetailTagsActions";
import useCustomerDetailTagsUI from "../../../hooks/inbox/ui/useCustomerDetailTagsUI";
import useCustomerDetailTagsForm from "../../../hooks/inbox/forms/useCustomerDetailTagsForm";
import { useTraceUpdate } from "../../../hooks/shared/useDebugRenders";

/**
 * Componente Molecule para la gestión y edición de tags del cliente en el inbox.
 *
 * Atomic Design: Molecule
 * - Compuesto por MultiSelectDropdownMC, ConversationTagsMC y ButtonComponent
 * - Permite seleccionar, crear y guardar tags asociados al cliente
 * - Utiliza TailwindCSS con .tw-wrapper
 * - Optimizado con memo y useCallback
 */
const CustomerDetailTagsMC = memo(({
  tags,
  customerId,
  platform
}) => {
  const tagsData = useCustomerDetailTagsData(tags);
  const tagsActions = useCustomerDetailTagsActions();
  const tagsUI = useCustomerDetailTagsUI(tags, tagsData.localCreatedTags, tagsActions.loading);
  const tagsForm = useCustomerDetailTagsForm(tagsData, tagsActions, tagsUI);

  const handleSave = useCallback(async () => {
    await tagsForm.handleSaveCustomerTags({
      customerId,
      platform,
      selectedTagsArg: tagsUI?.selectedTags,
      tagOptionsArg: tagsData.tagOptions,
      hasChangesArg: tagsUI?.hasChanges,
      refreshTagsArg: tagsData.refreshTags,
      exitEditModeArg: tagsUI?.exitEditMode,
      setLocalCreatedTagsArg: tagsData.setLocalCreatedTags
    });
  }, [
    tagsForm,
    customerId,
    platform,
    tagsUI?.selectedTags,
    tagsData.tagOptions,
    tagsUI?.hasChanges,
    tagsData.refreshTags,
    tagsUI?.exitEditMode,
    tagsData.setLocalCreatedTags
  ]);

  const handleEditClick = useCallback(() => {
    tagsUI?.handleEditModeToggle(handleSave);
  }, [tagsUI, handleSave]);

  useTraceUpdate('CustomerDetailTagsMC', {
    tagsLength: tags?.length,
    customerId,
    platform,
    isEditing: tagsUI?.isEditing,
    hasChanges: tagsUI?.hasChanges,
    loading: tagsActions.loading,
    localCreatedTagsLength: tagsData.localCreatedTags.length,
    selectedTagsLength: tagsUI?.selectedTags.length,
    canSave: tagsForm.canSave
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <div className="tw-wrapper tw-border-t tw-border-gray-200 tw-py-4">
      <div className="tw-flex tw-justify-between tw-items-center tw-flex-shrink-0">
        <span className="tw-text-sm tw-font-medium tw-text-gray-950">
          {tagsUI?.tagsTitle}
        </span>
        <div className="tw-flex tw-items-center tw-gap-2">
          <ButtonComponent
            {...tagsUI?.editButtonConfig}
            onClick={handleEditClick}
            data-tooltip-id={tagsUI?.tooltipId}
            disabled={tagsActions.loading}
          />
        </div>
        <ReactTooltip
          id={tagsUI?.tooltipId}
          className="tw-z-50"
        >
          <span>{tagsUI?.tooltipText}</span>
        </ReactTooltip>
      </div>
      {!tagsUI?.isEditing && tags.length > 0 && (
        <ConversationTagsMC tags={tags} />
      )}
      {tagsUI?.isEditing && (
        <>
          <div className="tw-relative tw-w-full tw-z-[60] tw-mb-2">
            <MultiSelectDropdownMC
              {...tagsUI?.multiSelectConfig}
              options={tagsData.tagOptions}
              defaultValue={tagsUI?.selectedTags}
              onSelect={tagsUI?.handleMultiSelectChange}
              onCreateOption={(tagName) => tagsForm.handleCreateTag(tagName, {
                checkTagExistsArg: tagsData.checkTagExists,
                setLocalCreatedTagsArg: tagsData.setLocalCreatedTags
              })}
            />
          </div>
          <div className="tw-flex">
            <ButtonComponent
              {...tagsUI?.saveButtonConfig}
              onClick={handleEditClick}
              disabled={tagsActions.loading}
            />
          </div>
        </>
      )}
    </div>
  );
});

CustomerDetailTagsMC.displayName = "CustomerDetailTagsMC";

export default CustomerDetailTagsMC;
